/**
 * Compute an effective view of the upload's aggregate progress that is
 * resilient to the `uploadTelemetryService` session getting frozen — the
 * root cause of bug 1 (2026-08): aggregate bar stuck at the resume seed
 * value (~15,500 KB) while per-file rows advanced to 35+ MB after a
 * throttle change.
 *
 * Why the underlying service can freeze:
 *   `uploadTelemetryService.recordProgress()` early-returns when the session
 *   status is PAUSED / COMPLETED / FAILED. So if a partial pause click set
 *   status = PAUSED but tus was not actually torn down (bytes still flowing),
 *   the aggregate value stays at whatever it was at pause-time even though
 *   per-file `updatePerFile` — which does not gate on status — keeps
 *   ticking. The main progress bar then diverges from the expanded per-file
 *   view for the rest of the upload.
 *
 * The resilient rule: aggregate = max(telemetry, sum-of-per-file). If the
 * per-file source has advanced past the telemetry snapshot, trust per-file.
 * This is monotonic on well-behaved uploads (per-file always tracks
 * telemetry) and it self-heals on frozen ones.
 *
 * Kept as a pure function so it is trivially unit-testable and can be
 * subbed in wherever the raw telemetry is read.
 *
 * @param {{ uploadedBytes?: number, totalBytes?: number, [key: string]: any } | null | undefined} telemetry
 * @param {Array<{ loaded?: number, sizeBytes?: number }> | null | undefined} files
 * @returns {typeof telemetry}
 */
export const computeEffectiveTelemetry = (telemetry, files) => {
  if (!telemetry) return telemetry;
  if (!Array.isArray(files) || files.length === 0) return telemetry;

  let perFileSum = 0;
  let perFileTotal = 0;
  for (const f of files) {
    // Clamp to sizeBytes so a stale `loaded` from an older subscribe frame
    // can't overshoot the file's true size and push the aggregate past 100%.
    const loaded = Math.max(0, Number(f?.loaded) || 0);
    const size = Math.max(0, Number(f?.sizeBytes) || 0);
    perFileSum += size > 0 ? Math.min(loaded, size) : loaded;
    perFileTotal += size;
  }

  const telemetryUploaded =
    typeof telemetry.uploadedBytes === "number" && telemetry.uploadedBytes > 0
      ? telemetry.uploadedBytes
      : 0;

  // Only override when per-file is *strictly larger* — matching values are
  // the common case and shouldn't churn the memoised text. Also ignores the
  // rare boot-time frame where per-file is empty (sum=0) but telemetry has
  // already published a starting value.
  if (perFileSum <= telemetryUploaded) return telemetry;

  return {
    ...telemetry,
    uploadedBytes: perFileSum,
    // If telemetry lost its total (rare — happens if start() ran without a
    // totalBytes hint), backfill from per-file so the "of X MB" side of the
    // caption doesn't render as null.
    totalBytes:
      typeof telemetry.totalBytes === "number" && telemetry.totalBytes > 0
        ? telemetry.totalBytes
        : perFileTotal || telemetry.totalBytes,
    // When we hit this branch, telemetry is stuck (usually PAUSED with tus
    // still flushing). Its rollingBps / averageBps values are frozen at
    // whatever they were when the freeze started — the user's report was
    // "speed is around 5 KB/s but that's not true". Better to render nothing
    // than to render a lie: null-out the speed values so formatSpeed → null
    // → the speed row hides itself.
    rollingBps: null,
    averageBps: null,
  };
};
